import ExercisesContext from "./ExercisesContext";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ExercisesContextType } from "./ExercisesProvider.types";
import ExerciseMetadataService from "services/ExerciseMetadataService";
import useInfoMessage from "hooks/useInfoMessage";
import { Exercise } from "types";
import CustomExercisesActionsContext from "./CustomExercisesActionsContext";
import CustomExerciseService from "services/CustomExerciseService";
import ExerciseHelpers from "helpers/ExerciseHelpers";
import { CustomExercise } from "types/custom-exercise";
import FilteredExercisesContext from "providers/ExercisesProvider/FilteredExercisesContext";
import useCanDo from "hooks/useCanDo";
import { useTranslation } from "react-i18next";
import BodySegmentsProvider from "./BodySegmentsContext";
import EquipmentsProvider from "./EquipmentsContext";
import useNaturalSort from "hooks/useNaturalSort";
import useStatus from "hooks/useStatus";
import ExercisesContextStatus from "./ExercisesContextStatus";

interface ExercisesProviderProps {
  children?: React.ReactNode;
}

function ExercisesProvider(props: ExercisesProviderProps) {
  const [exercises, setExercises] = useState<ExercisesContextType>([]);
  const customExercisesFeatureEnabled = useCanDo("library.any");
  const { t } = useTranslation();
  const onError = useInfoMessage({ type: "error" });
  const naturalCompare = useNaturalSort();
  const { status, setStatus } = useStatus();

  /**
   * @description Handle the translation of all exercises
   * If custom we take the name from the custom exercise
   * otherwise we take the name from the translation file `exercise_name_${exercise.id}`
   * If not found we take the default name
   *
   * @important This is a special case to handle the translation, sorting and filtering of exercises
   * We should always translate as close as the render as possible
   */

  const handleTranslateExercise = useCallback<<T extends Exercise>(exercise: T) => T>(
    (exercise) => {
      /**
       * Category key is used to translate the category name
       * We remove all commas and replace spaces with underscores
       * This shouldn't be used for anything else than translation
       */
      const categoryKey = exercise.category.replace(/,/g, "").replace(/\s+/g, "_").toUpperCase();

      /**
       * Variants are translated at render time
       */
      const commonTranslatedValues = {
        ...exercise,
        category: t("exercise_category_name", { defaultValue: exercise.category, context: categoryKey }),
        categoryId: exercise.category,
        repetitions: exercise.repetitions.map((repetition) => ({
          ...repetition,
          name: t("exercise_rep_metric", { defaultValue: repetition.name, context: repetition.field }),
        })),
      };

      if (exercise.type === "Custom") {
        return {
          ...exercise,
          ...commonTranslatedValues,
          metrics: exercise.metrics.map((metric) => {
            /**
             *  Custom exercises can have weight and repCount metrics.
             * We need to translate them as well, the rest is set by the user
             * */
            if (["weight", "repCount"].includes(metric.field)) {
              return {
                ...metric,
                name: t("exercise_metric", { defaultValue: metric.name, context: metric.field }),
              };
            }
            return metric;
          }),
        };
      }

      return {
        ...exercise,
        ...commonTranslatedValues,
        name: t("exercise_name", { defaultValue: exercise.name, context: exercise.id }),
        metrics: exercise.metrics.map((metric) => ({
          ...metric,
          name: t("exercise_metric", { defaultValue: metric.name, context: metric.field }),
        })),
      };
    },
    [t]
  );

  const customExerciseActions = useMemo(() => {
    const upsertExercise = (upsertedExercise: Exercise) =>
      setExercises((prevExercises) => {
        const filteredExercises = prevExercises.filter((exercise) => exercise.id !== upsertedExercise.id);
        const resolvedExercise = ExerciseHelpers.inheritMinimumVelocityThreshold(upsertedExercise, (id) =>
          prevExercises.find((exercise) => exercise.id === id)
        );

        return [...filteredExercises, resolvedExercise];
      });

    const getById: typeof CustomExerciseService.getById = async (id: string) => {
      const exercise = await CustomExerciseService.getById(id);
      const translatedExercise = handleTranslateExercise(exercise);
      upsertExercise(translatedExercise);

      return exercise;
    };

    const create: typeof CustomExerciseService.create = async (newCustomExercise) => {
      const newExercise = await CustomExerciseService.create(newCustomExercise);
      const translatedExercise = handleTranslateExercise<CustomExercise>(newExercise);
      upsertExercise(translatedExercise);

      return translatedExercise;
    };

    const update: typeof CustomExerciseService.update = async (id, updatedCustomExercise) => {
      const updatedExercise = await CustomExerciseService.update(id, updatedCustomExercise);

      upsertExercise(updatedExercise);

      return updatedExercise;
    };

    const deleteExercise: typeof CustomExerciseService.delete = async (id: string) => {
      await CustomExerciseService.delete(id);

      setExercises((prevExercises) => prevExercises.filter((exercise) => exercise.id !== id));

      return { id };
    };

    return { getById, create, update, delete: deleteExercise };
  }, [handleTranslateExercise]);

  useEffect(() => {
    (async () => {
      try {
        const exercises = await ExerciseMetadataService.get();
        setStatus("success");
        setExercises(exercises.map(handleTranslateExercise));
      } catch (error: any) {
        onError({ message: error.message });
      }
    })();
  }, [handleTranslateExercise, onError, setStatus]);

  const active = useMemo(() => {
    const active: Exercise[] = [];

    ExerciseHelpers.sort(exercises, naturalCompare).forEach((exercise) => {
      if (["Speed & Agility"].includes(exercise.categoryId)) return;

      if (exercise.type === "Custom" && customExercisesFeatureEnabled === false) {
        return;
      } else {
        active.push(exercise);
      }
    });

    return active;
  }, [naturalCompare, customExercisesFeatureEnabled, exercises]);

  return (
    <CustomExercisesActionsContext.Provider value={customExerciseActions}>
      <ExercisesContext.Provider value={active}>
        <FilteredExercisesContext.Provider value={active}>
          <ExercisesContextStatus.Provider value={status}>
            <BodySegmentsProvider>
              <EquipmentsProvider>{props.children}</EquipmentsProvider>
            </BodySegmentsProvider>
          </ExercisesContextStatus.Provider>
        </FilteredExercisesContext.Provider>
      </ExercisesContext.Provider>
    </CustomExercisesActionsContext.Provider>
  );
}

export default ExercisesProvider;
